import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://oopbuyspread.hair';
const FAVICON_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';

interface ArticleContent {
  heading: string;
  paragraphs: string[];
  listItems?: string[];
}

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  heroImage: string;
  heroAlt: string;
  breadcrumbLabel: string;
  badge: string;
  readingTime: string;
  sections: ArticleContent[];
  ctaTitle: string;
  ctaText: string;
}

export default function ArticleLayout({
  title,
  subtitle,
  heroImage,
  heroAlt,
  breadcrumbLabel,
  badge,
  readingTime,
  sections,
  ctaTitle,
  ctaText,
}: ArticleLayoutProps) {
  const location = useLocation();
  const articleUrl = `${SITE_URL}${location.pathname}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: subtitle,
    image: heroImage,
    datePublished: '2026-07-01',
    dateModified: '2026-07-15',
    author: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: breadcrumbLabel,
        item: articleUrl,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">
                  Startseite
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-900 font-medium">
                {breadcrumbLabel}
              </li>
            </ol>
          </nav>

          <header className="mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
              {badge}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {subtitle}
            </p>
            <div className="flex items-center gap-3 mt-6 text-xs text-foreground-400">
              <span>Aktualisiert: Juli 2026</span>
              <span>·</span>
              <span>Lesezeit: ~{readingTime} Minuten</span>
            </div>
          </header>

          <div className="w-full h-72 md:h-96 rounded-xl overflow-hidden mb-10 md:mb-14">
            <img
              src={heroImage}
              alt={heroAlt}
              title={heroAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom text-sm md:text-base leading-relaxed text-foreground-700 space-y-8">
            {sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p
                    key={pIdx}
                    className={pIdx < section.paragraphs.length - 1 ? 'mb-4' : ''}
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}
                {section.listItems && section.listItems.length > 0 && (
                  <ul className="list-disc pl-5 space-y-3 mt-4">
                    {section.listItems.map((item, lIdx) => (
                      <li key={lIdx}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <div className="bg-background-100 rounded-xl p-6 md:p-8 text-center mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                {ctaTitle}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6">
                {ctaText}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Zurück zur Startseite
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 text-sm font-medium bg-foreground-900 text-white rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  oopbuy spreadsheet
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}